"use client";

import { useCallback } from "react";
import Marquee from "~/componentsDS/Marquee";
import Heading from "~/componentsDS/Typography/Heading";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import Container from "~/componentsDS/Layout/Container";
import Carousel from "~/componentsDS/Carousel";
import {
    type CarouselApi,
    CarouselContentVisible,
    CarouselItem,
} from "~/components/ui/carousel";
import Reveal from "~/componentsDS/Reveal";
import { useQuizLink } from "~/components/features/quiz";
import { QUIZ_ROUTES } from "~/constants/quizRoutes";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import ProductOverviewCard from "./ProductOverviewCard";
import SymptomRecommendationCard from "./SymptomRecommendationCard";
import { PRODUCTS, RECOMMENDATIONS } from "./data";

export default function WomenDeserveAnswersSection() {
    const sendEvent = useTrackEvent();
    const { href: oestraHref } = useQuizLink(QUIZ_ROUTES.HORMONE_IMBALANCE);
    const { href: libidaHref } = useQuizLink(QUIZ_ROUTES.LIBIDA);
    const { href: nadHref } = useQuizLink(QUIZ_ROUTES.NAD);

    const resolvedLinks: Record<string, string> = {
        [QUIZ_ROUTES.HORMONE_IMBALANCE]: oestraHref,
        [QUIZ_ROUTES.LIBIDA]: libidaHref,
        [QUIZ_ROUTES.NAD]: nadHref,
    };

    const handleSelect = useCallback((api: CarouselApi) => {
        if (!api) return;

        const index = api.selectedScrollSnap();
        const product = PRODUCTS[index];

        if (!product) return;

        sendEvent({
            event: "user_interaction",
            value: {
                event_name: GTM_EVENT_NAMES.HP_INTERACTION,
                widget: "what_you_need_to_know",
                meta_data: {
                    image_title: product.imageTitle,
                    image_count: index + 1,
                    product_name: product.analyticEvent.product_name,
                },
            },
        });
    }, [sendEvent]);

    return (
        <section className="py-8 lg:pt-0 lg:pb-24 bg-gray100 md:bg-transparent rounded-3xl overflow-hidden">
            <Container>
                <Reveal>
                    <Heading as="h2" size="sm" sizes={{ md: "md" }} className="text-primary500 md:text-center mb-4">Get the answers <br className="md:hidden" />all women deserve</Heading>
                </Reveal>
                <Paragraph size="lg" className="text-primary500 md:text-center mb-6">Discover targeted treatments built around the real issues affecting women’s health.</Paragraph>
            </Container>

            <div className="box-content w-full overflow-hidden mb-2 md:mb-5">
                <Carousel
                    loop={true}
                    variant="compact"
                    desktopWheelGestures
                    onSelect={handleSelect}
                    opts={{
                        align: "center",
                        skipSnaps: false,
                        dragFree: false
                    }}
                    compactPaginationAlign="right9"
                    className="w-full"
                >
                    <CarouselContentVisible className="max-md:-ml-2 md:-ml-5">
                        {PRODUCTS.map((product, index) => (
                            <CarouselItem
                                key={index}
                                className="pl-2 md:pl-5 flex-shrink-0 basis-[90vw] xl:basis-[1216px]"
                            >
                                <ProductOverviewCard
                                    {...product}
                                    cta={{
                                        ...product.cta,
                                        href: resolvedLinks[product.cta.href] ?? product.cta.href,
                                    }}
                                />
                            </CarouselItem>
                        ))}
                    </CarouselContentVisible>
                </Carousel>
            </div>

            <Marquee allowDrag speed={1} direction="left" pauseOnHover={true}>
                <div className="flex shrink-0 items-center gap-2 px-1 md:gap-[20px] md:px-[10px]">
                    {RECOMMENDATIONS.map((card, index) => (
                        <div key={index} className="shrink-0 w-[352px] md:w-[400px]">
                            <SymptomRecommendationCard
                                {...card}
                                link={resolvedLinks[card.link] ?? card.link}
                            />
                        </div>
                    ))}
                </div>
            </Marquee>
        </section>
    )
};
